import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BRAND, mailtoLink } from "@/data/site";

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-ink mt-14 mb-5 leading-tight">
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="font-serif text-xl md:text-2xl text-ink mt-10 mb-3 leading-tight">
    {children}
  </h3>
);

const P = ({ children }) => (
  <p className="text-ink/75 leading-relaxed mb-4 text-[15px]">{children}</p>
);

const UL = ({ children }) => (
  <ul className="list-disc list-outside pl-6 marker:text-gold space-y-2 mb-4 text-ink/75 text-[15px] leading-relaxed">
    {children}
  </ul>
);

const Voorwaarden = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-40 md:pt-48 pb-14 bg-beige">
        <div className="container-x text-center max-w-3xl mx-auto">
          <p className="eyebrow mb-4">Juridisch</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink">
            Algemene voorwaarden, <span className="italic text-gold">privacybeleid</span> &amp;
            toestemming fotografie
          </h1>
          <p className="mt-6 text-sm text-ink/60 tracking-wider">
            Lenslopers Fotografie — Laatst bijgewerkt: 12 augustus 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white py-16 md:py-24">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="container-narrow max-w-3xl"
          data-testid="voorwaarden-content"
        >
          {/* Company block */}
          <div className="bg-beige p-6 md:p-8 border-l-2 border-gold text-[15px] text-ink/80 leading-relaxed">
            <p className="font-serif text-lg text-ink mb-2">Lenslopers Fotografie</p>
            <p>Lemmens, Eric</p>
            <p>Ondernemingsnummer: BE0807.873.408</p>
            <p>
              E-mail:{" "}
              <a href={mailtoLink("Vraag algemene voorwaarden")} className="text-gold hover:underline">
                {BRAND.email}
              </a>
            </p>
            <p>
              Website:{" "}
              <a href="https://www.lenslopers.com" className="text-gold hover:underline">
                www.lenslopers.com
              </a>
            </p>
          </div>

          <H2>1. Algemeen</H2>
          <P>
            Deze pagina bevat de algemene voorwaarden van Lenslopers Fotografie, evenals informatie
            over privacy, persoonsgegevens, cookies, auteursrechten en het gebruik en de publicatie
            van foto's.
          </P>
          <P>
            Deze voorwaarden zijn van toepassing op alle diensten van Lenslopers, waaronder
            fotoshoots, portretfotografie, gezinsfotografie, baby- en zwangerschapsfotografie,
            huwelijksfotografie, communiefotografie, evenementenfotografie, bedrijfsfotografie,
            dierenfotografie, mini-sessies, fotobooth/Magic Mirror-verhuur en de levering van
            digitale foto's en fotoproducten.
          </P>
          <P>
            Door een opdracht te boeken, verklaart de klant kennis te hebben genomen van de
            toepasselijke voorwaarden.
          </P>

          <H2>2. Reservatie en overeenkomst</H2>
          <P>
            Een reservatie wordt definitief nadat Lenslopers de opdracht heeft bevestigd en, indien
            van toepassing, het afgesproken voorschot is ontvangen.
          </P>
          <P>Een datum is pas definitief gereserveerd nadat Lenslopers de reservatie heeft bevestigd.</P>
          <P>
            Offertes zijn geldig gedurende de vermelde geldigheidsperiode. Indien geen
            geldigheidsperiode wordt vermeld, kan Lenslopers een offerte aanpassen zolang de
            opdracht nog niet definitief is bevestigd.
          </P>

          <H2>3. Betaling</H2>
          <P>De betalingstermijn wordt vermeld op de offerte of factuur.</P>
          <P>Wanneer een voorschot wordt gevraagd, dient dit binnen de afgesproken termijn te worden betaald.</P>
          <P>Bij laattijdige betaling kan Lenslopers de wettelijk toegestane interesten en kosten aanrekenen.</P>

          <H2>4. Annulering en verplaatsing</H2>
          <P>Een annulering dient zo snel mogelijk schriftelijk te worden gemeld.</P>
          <P>
            Indien voor een specifieke opdracht afzonderlijke annuleringsvoorwaarden zijn
            overeengekomen, gelden deze voorwaarden.
          </P>
          <P>
            Bij annulering kan, afhankelijk van het tijdstip van annulering, de aard van de opdracht
            en reeds gemaakte kosten, een redelijke vergoeding worden aangerekend.
          </P>
          <P>Wanneer mogelijk wordt samen met de klant gezocht naar een nieuwe datum.</P>
          <P>Bij slecht weer voor een buitenshoots kan de afspraak in overleg worden verplaatst.</P>

          <H2>5. Uitvoering van de opdracht</H2>
          <P>Lenslopers bepaalt de fotografische en artistieke uitvoering van de opdracht.</P>
          <P>De klant verklaart kennis te hebben genomen van de fotografische stijl van Lenslopers.</P>
          <P>
            De selectie van foto's, compositie, kleurcorrectie en nabewerking gebeurt door
            Lenslopers.
          </P>
          <P>
            Een specifiek beeld, pose, locatie of aantal foto's wordt alleen gegarandeerd wanneer
            dit vooraf schriftelijk is overeengekomen.
          </P>
          <P>
            RAW-bestanden worden niet geleverd, tenzij hierover vooraf schriftelijk een andere
            afspraak is gemaakt.
          </P>

          <H2>6. Levering</H2>
          <P>De leveringstermijn is afhankelijk van het type opdracht.</P>
          <P>
            Lenslopers streeft naar een zo spoedig mogelijke levering. Bij grotere opdrachten, zoals
            huwelijken en evenementen, kan de verwerkingstijd langer zijn.
          </P>
          <P>
            De klant is zelf verantwoordelijk voor het bewaren van een back-up van de geleverde
            digitale foto's.
          </P>

          <H2>7. Auteursrecht en gebruiksrecht</H2>
          <P>Alle foto's die door Lenslopers worden gemaakt, blijven auteursrechtelijk beschermd.</P>
          <P>
            Na volledige betaling ontvangt de klant een gebruiksrecht voor persoonlijk gebruik,
            tenzij schriftelijk anders overeengekomen.
          </P>
          <P>Zonder voorafgaande toestemming mogen foto's niet:</P>
          <UL>
            <li>commercieel worden gebruikt;</li>
            <li>worden verkocht;</li>
            <li>aan derden worden gelicentieerd;</li>
            <li>ingrijpend worden bewerkt;</li>
            <li>van het watermerk worden ontdaan;</li>
            <li>worden gebruikt voor reclame of promotie door derden.</li>
          </UL>
          <P>Voor commercieel gebruik kunnen afzonderlijke afspraken en tarieven gelden.</P>

          <div className="mt-16 pb-2 border-b border-gold/40">
            <p className="eyebrow text-gold">Privacy &amp; AVG/GDPR</p>
          </div>

          <H2>8. Verwerkingsverantwoordelijke</H2>
          <P>De verwerkingsverantwoordelijke voor persoonsgegevens is:</P>
          <div className="bg-beige p-5 my-5 text-[15px] text-ink/80 leading-relaxed">
            <p>Lemmens, Eric — Lenslopers Fotografie</p>
            <p>Ondernemingsnummer: BE0807.873.408</p>
            <p>E-mail: {BRAND.email}</p>
          </div>
          <P>
            Lenslopers verwerkt persoonsgegevens overeenkomstig de Algemene Verordening
            Gegevensbescherming (AVG/GDPR) en de toepasselijke Belgische wetgeving.
          </P>

          <H2>9. Welke persoonsgegevens verwerken wij?</H2>
          <P>Afhankelijk van de dienst kunnen wij onder andere verwerken:</P>
          <UL>
            <li>naam en voornaam;</li>
            <li>adres;</li>
            <li>e-mailadres;</li>
            <li>telefoonnummer;</li>
            <li>facturatiegegevens;</li>
            <li>communicatie met de klant;</li>
            <li>gegevens over de geboekte opdracht;</li>
            <li>foto's waarop personen herkenbaar zijn.</li>
          </UL>
          <P>Wij verzamelen alleen gegevens die noodzakelijk zijn voor de betreffende doeleinden.</P>

          <H2>10. Waarvoor gebruiken wij persoonsgegevens?</H2>
          <P>Persoonsgegevens kunnen worden gebruikt voor:</P>
          <UL>
            <li>communicatie;</li>
            <li>offertes en reservaties;</li>
            <li>planning;</li>
            <li>uitvoering van opdrachten;</li>
            <li>facturatie en administratie;</li>
            <li>betalingen;</li>
            <li>levering van foto's en fotoproducten;</li>
            <li>klantenservice;</li>
            <li>wettelijke verplichtingen;</li>
            <li>beveiliging van website en systemen;</li>
            <li>marketing wanneer daarvoor een geldige rechtsgrond bestaat.</li>
          </UL>
          <P>
            De verwerking kan gebaseerd zijn op de uitvoering van een overeenkomst, een wettelijke
            verplichting, toestemming of een gerechtvaardigd belang wanneer aan de wettelijke
            voorwaarden is voldaan.
          </P>

          <H2>11. Bewaring van gegevens</H2>
          <P>
            Persoonsgegevens worden niet langer bewaard dan noodzakelijk voor het doel waarvoor ze
            zijn verzameld, tenzij een wettelijke bewaarplicht een langere termijn vereist.
          </P>
          <P>Boekhoudkundige en fiscale gegevens worden bewaard gedurende de wettelijk vereiste termijn.</P>
          <P>Digitale foto's kunnen gedurende een bepaalde periode worden gearchiveerd.</P>
          <P>De klant blijft verantwoordelijk voor het bewaren van een eigen back-up van de geleverde foto's.</P>

          <H2>12. Delen van persoonsgegevens</H2>
          <P>Lenslopers verkoopt geen persoonsgegevens.</P>
          <P>
            Wanneer noodzakelijk voor de uitvoering van een opdracht kunnen gegevens worden verwerkt
            door externe dienstverleners, zoals hostingproviders, boekhoudkundige dienstverleners,
            betalingsproviders, fotolabs, drukkerijen, online galerijen, opslagdiensten en
            softwareleveranciers.
          </P>
          <P>Deze partijen ontvangen uitsluitend gegevens die noodzakelijk zijn voor hun dienstverlening.</P>

          <div className="mt-16 pb-2 border-b border-gold/40">
            <p className="eyebrow text-gold">Foto's &amp; recht op afbeelding</p>
          </div>

          <H2>13. Maken en publiceren van foto's</H2>
          <P>
            Het recht om iemand te fotograferen en het recht om een foto later te publiceren zijn
            niet automatisch hetzelfde.
          </P>
          <P>
            De Belgische Gegevensbeschermingsautoriteit benadrukt dat toestemming voor het maken
            van een foto en toestemming voor het latere gebruik of de publicatie ervan afzonderlijk
            moeten worden beoordeeld.
          </P>
          <P>
            Lenslopers gebruikt herkenbare foto's voor website, portfolio, sociale media en
            promotionele doeleinden alleen wanneer daarvoor een passende rechtsgrond bestaat.
          </P>

          <H2>14. Toestemming voor publicatie</H2>
          <P>Toestemming voor publicatie is vrijwillig en staat los van de boeking van een fotoshoot.</P>
          <P>
            Wanneer toestemming wordt gevraagd, wordt deze afzonderlijk gegeven voor het gebruik
            van herkenbare foto's voor bijvoorbeeld:
          </P>
          <UL>
            <li>www.lenslopers.com;</li>
            <li>Instagram;</li>
            <li>Facebook;</li>
            <li>portfolio;</li>
            <li>flyers en brochures;</li>
            <li>advertenties;</li>
            <li>ander promotioneel materiaal van Lenslopers.</li>
          </UL>
          <P>
            Toestemming moet vrij, specifiek, geïnformeerd en ondubbelzinnig worden gegeven. Een
            geldige toestemming kan later worden ingetrokken.
          </P>
          <P>Een intrekking heeft geen invloed op verwerkingen die vóór de intrekking rechtmatig zijn uitgevoerd.</P>

          <H2>15. Evenementen en groepsfoto's</H2>
          <P>Bij evenementen kunnen sfeerbeelden, overzichtsfoto's en groepsfoto's worden gemaakt.</P>
          <P>
            Wanneer personen slechts een ondergeschikt onderdeel vormen van een algemene sfeerfoto
            of menigte, kan een andere beoordeling gelden dan wanneer iemand specifiek als
            hoofdonderwerp wordt gefotografeerd.
          </P>
          <P>Voor gerichte foto's van herkenbare personen wordt, wanneer vereist, toestemming gevraagd.</P>
          <P>
            De organisator van een evenement kan verantwoordelijk zijn voor het vooraf informeren
            van bezoekers en deelnemers over de aanwezigheid van een fotograaf.
          </P>

          <H2>16. Minderjarigen</H2>
          <P>
            Bij foto's van minderjarigen wordt rekening gehouden met hun leeftijd, maturiteit,
            belangen en het toepasselijke recht.
          </P>
          <P>
            Wanneer toestemming van een ouder of wettelijke vertegenwoordiger vereist is, moet deze
            persoon bevoegd zijn om toestemming te geven.
          </P>
          <P>
            Bij twijfel kan Lenslopers toestemming vragen van zowel de minderjarige als de ouder of
            wettelijke vertegenwoordiger.
          </P>

          <div className="mt-16 pb-2 border-b border-gold/40">
            <p className="eyebrow text-gold">Cookies</p>
          </div>

          <H2>17. Cookies op de website</H2>
          <P>De website van Lenslopers kan gebruikmaken van cookies en vergelijkbare technieken.</P>
          <P>
            Strikt noodzakelijke cookies kunnen worden gebruikt wanneer deze noodzakelijk zijn voor
            de werking of beveiliging van de website.
          </P>
          <P>
            Voor niet-noodzakelijke cookies, waaronder bepaalde analytische, tracking- of
            marketingcookies, wordt vooraf toestemming gevraagd wanneer dit wettelijk vereist is.
          </P>
          <P>
            Het verder bezoeken of scrollen op de website geldt niet als geldige toestemming voor
            niet-noodzakelijke cookies.
          </P>
          <P>
            Wanneer dergelijke cookies worden gebruikt, moet de bezoeker de mogelijkheid krijgen om
            toestemming te geven of te weigeren en deze keuze later opnieuw aan te passen.
          </P>

          <div className="mt-16 pb-2 border-b border-gold/40">
            <p className="eyebrow text-gold">Rechten van de klant</p>
          </div>

          <H2>18. Uw rechten onder de AVG</H2>
          <P>Afhankelijk van de omstandigheden heeft u onder meer recht op:</P>
          <UL>
            <li>inzage in uw persoonsgegevens;</li>
            <li>verbetering van onjuiste gegevens;</li>
            <li>verwijdering van gegevens;</li>
            <li>beperking van verwerking;</li>
            <li>bezwaar tegen bepaalde verwerkingen;</li>
            <li>overdraagbaarheid van gegevens wanneer aan de wettelijke voorwaarden is voldaan;</li>
            <li>intrekking van toestemming wanneer de verwerking daarop gebaseerd is.</li>
          </UL>
          <P>Deze rechten zijn niet absoluut en kunnen onder bepaalde wettelijke voorwaarden worden beperkt.</P>
          <P>
            Een verzoek kan worden gestuurd naar:{" "}
            <a href={mailtoLink("AVG-verzoek")} className="text-gold hover:underline">
              {BRAND.email}
            </a>
          </P>
          <P>Om persoonsgegevens te beschermen kan Lenslopers vragen om uw identiteit te bevestigen.</P>

          <H2>19. Intrekken van toestemming voor foto's</H2>
          <P>Wilt u uw toestemming voor het gebruik van foto's intrekken?</P>
          <P>
            Stuur dan een e-mail naar{" "}
            <a href={mailtoLink("Intrekking toestemming foto's")} className="text-gold hover:underline">
              {BRAND.email}
            </a>{" "}
            met uw naam en, indien mogelijk, de betreffende fotoshoot of foto's.
          </P>
          <P>
            Lenslopers zal na ontvangst van een geldig verzoek toekomstige publicatie op basis van
            de ingetrokken toestemming stopzetten, voor zover dit redelijkerwijs mogelijk is.
          </P>
          <P>Reeds gedrukte materialen of reeds verspreide publicaties kunnen niet altijd worden teruggenomen.</P>

          <H2>20. Klachten over privacy</H2>
          <P>
            Heeft u een klacht over de verwerking van uw persoonsgegevens, neem dan eerst contact
            op met Lenslopers.
          </P>
          <P>U kunt daarnaast een klacht indienen bij:</P>
          <div className="bg-beige p-5 my-5 text-[15px] text-ink/80 leading-relaxed">
            <p>Gegevensbeschermingsautoriteit (GBA)</p>
            <p>Drukpersstraat 35</p>
            <p>1000 Brussel</p>
            <p>België</p>
          </div>

          <div className="mt-16 pb-2 border-b border-gold/40">
            <p className="eyebrow text-gold">Overige voorwaarden</p>
          </div>

          <H2>21. Cadeaubonnen</H2>
          <P>
            Cadeaubonnen zijn niet inwisselbaar voor contant geld en zijn geldig gedurende de
            vermelde geldigheidsperiode.
          </P>
          <P>Bij verlies kan een cadeaubon niet automatisch worden vervangen.</P>

          <H2>22. Verhuurvoorwaarden Magic Mirror fotobooth</H2>
          <P>
            Voor de verhuur van de Magic Mirror fotobooth gelden onderstaande specifieke
            verhuurvoorwaarden. Deze zijn ook terug te vinden op een{" "}
            <Link to="/verhuurvoorwaarden-magic-mirror" className="text-gold hover:underline">
              afzonderlijke pagina
            </Link>
            .
          </P>

          <H3>22.1 Reservatie en betaling</H3>
          <UL>
            <li>Een reservatie is definitief na betaling van een voorschot van €150.</li>
            <li>
              Het voorschot dient binnen 7 dagen na goedkeuring van de aanvraag betaald te zijn om
              de datum te behouden.
            </li>
            <li>Het resterende bedrag dient op de dag van het evenement betaald te zijn.</li>
            <li>De schadewaarborg bedraagt €300.</li>
          </UL>

          <H3>22.2 Waarborg en schade</H3>
          <UL>
            <li>De waarborg van €300 staat los van de huurprijs en het voorschot.</li>
            <li>
              De huurder is gedurende de volledige huurperiode verantwoordelijk voor de Magic
              Mirror, printer, accessoires en overige ter beschikking gestelde materialen.
            </li>
            <li>
              De huurder is aansprakelijk voor schade, verlies of diefstal veroorzaakt door de
              huurder, gasten of andere aanwezigen.
            </li>
            <li>
              Bij schade worden de werkelijke herstel- of vervangingskosten met de waarborg
              verrekend.
            </li>
            <li>
              Indien de schade hoger is dan de waarborg, wordt het resterende bedrag afzonderlijk
              aan de huurder aangerekend.
            </li>
            <li>Normale slijtage en normale gebruikssporen worden niet als schade beschouwd.</li>
            <li>
              Indien er geen schade, verlies of ontbrekende materialen worden vastgesteld, wordt de
              waarborg volledig terugbetaald.
            </li>
          </UL>

          <H3>22.3 Gebruik van de Magic Mirror</H3>
          <UL>
            <li>
              De Magic Mirror mag uitsluitend worden gebruikt tijdens de afgesproken huurperiode.
            </li>
            <li>De booth mag niet worden verplaatst zonder toestemming van Lenslopers.</li>
            <li>De huurder zorgt voor een veilige, droge en geschikte opstelplaats.</li>
            <li>Kinderen gebruiken de Magic Mirror onder toezicht van een volwassene.</li>
            <li>
              De apparatuur mag niet worden blootgesteld aan regen, vocht, rook, confetti,
              vloeistoffen of andere omstandigheden die schade kunnen veroorzaken.
            </li>
          </UL>

          <H3>22.4 Stroomvoorziening</H3>
          <UL>
            <li>
              De huurder voorziet een geschikte 230V-stroomvoorziening binnen maximaal 5 meter van
              de opstelplaats.
            </li>
            <li>De stroomvoorziening dient veilig en betrouwbaar te zijn.</li>
          </UL>

          <H3>22.5 Opbouw en afbraak</H3>
          <UL>
            <li>Lenslopers verzorgt de opbouw en afbraak van de Magic Mirror.</li>
            <li>De opbouw gebeurt ongeveer 45 minuten vóór aanvang van de huurperiode.</li>
            <li>
              Na afloop moet de booth vrij toegankelijk zijn zodat deze veilig kan worden afgebroken
              en meegenomen.
            </li>
          </UL>

          <H3>22.6 Foto's en gebruik ervan</H3>
          <UL>
            <li>
              De gemaakte foto's mogen door Lenslopers worden gebruikt voor portfolio, website,
              sociale media en andere promotionele doeleinden, tenzij de huurder vooraf
              uitdrukkelijk aangeeft dat dit niet gewenst is.
            </li>
            <li>
              Wanneer de huurder geen toestemming geeft voor promotioneel gebruik, worden de foto's
              niet voor deze doeleinden gebruikt.
            </li>
          </UL>

          <H3>22.7 Technische problemen</H3>
          <UL>
            <li>
              Lenslopers zorgt ervoor dat de apparatuur vóór gebruik wordt gecontroleerd en correct
              wordt opgesteld.
            </li>
            <li>
              Bij technische problemen wordt alles in het werk gesteld om deze zo snel mogelijk op
              te lossen.
            </li>
            <li>
              Lenslopers kan niet aansprakelijk worden gesteld voor technische problemen die het
              gevolg zijn van omstandigheden buiten haar controle, zoals stroomuitval,
              internetproblemen, externe apparatuur of onoordeelkundig gebruik.
            </li>
          </UL>

          <H3>22.8 Annulatie</H3>
          <UL>
            <li>Bij annulatie door de huurder wordt het betaalde voorschot niet terugbetaald.</li>
            <li>
              Bij annulatie door Lenslopers wordt het reeds betaalde bedrag terugbetaald, tenzij in
              overleg een andere datum wordt vastgelegd.
            </li>
          </UL>

          <H3>22.9 Verplaatsing en transport</H3>
          <UL>
            <li>Verplaatsing is gratis tot 10 km rond Kieldrecht.</li>
            <li>Boven 10 km wordt een kilometervergoeding van €0,45 per km aangerekend.</li>
            <li>De kilometervergoeding wordt berekend voor de volledige heen- en terugrit.</li>
          </UL>

          <H3>22.10 Aansprakelijkheid</H3>
          <UL>
            <li>
              De huurder zorgt ervoor dat gasten de Magic Mirror op een correcte en zorgvuldige
              manier gebruiken.
            </li>
            <li>
              Lenslopers is niet aansprakelijk voor lichamelijke schade of schade aan persoonlijke
              eigendommen van gasten die ontstaat door het gebruik van de booth, behoudens gevallen
              waarin Lenslopers hiervoor wettelijk aansprakelijk is.
            </li>
          </UL>

          <H3>22.11 Akkoord met de voorwaarden</H3>
          <P>
            Door een reservatie te bevestigen en het voorschot te betalen, verklaart de huurder
            zich akkoord met bovenstaande verhuurvoorwaarden.
          </P>

          <H2>23. Overmacht</H2>
          <P>
            Lenslopers kan niet aansprakelijk worden gesteld wanneer een opdracht geheel of
            gedeeltelijk niet kan worden uitgevoerd door omstandigheden buiten haar redelijke
            controle.
          </P>
          <P>
            Daaronder vallen onder andere ziekte, ongeval, technische problemen, brand, extreme
            weersomstandigheden, stroomuitval en overheidsmaatregelen.
          </P>
          <P>Wanneer mogelijk wordt samen met de klant naar een passende oplossing gezocht.</P>

          <H2>24. Aansprakelijkheid</H2>
          <P>Lenslopers voert opdrachten met de nodige zorg uit.</P>
          <P>
            Voor zover wettelijk toegestaan, is de aansprakelijkheid van Lenslopers beperkt tot het
            bedrag dat voor de betreffende opdracht werd betaald.
          </P>
          <P>Deze beperking geldt niet wanneer een beperking van aansprakelijkheid wettelijk niet is toegestaan.</P>

          <H2>25. Klachten</H2>
          <P>
            Klachten over de dienstverlening of geleverde producten dienen binnen een redelijke
            termijn na vaststelling schriftelijk te worden gemeld.
          </P>
          <P>Lenslopers zal proberen iedere klacht in overleg met de klant op te lossen.</P>

          <H2>26. Wijzigingen</H2>
          <P>
            Lenslopers kan deze voorwaarden aanpassen wanneer de dienstverlening, website of
            toepasselijke regelgeving verandert.
          </P>
          <P>De meest recente versie wordt steeds op deze pagina gepubliceerd.</P>
          <P>
            Voor reeds gesloten overeenkomsten blijft in principe de versie gelden die van
            toepassing was bij het sluiten van de overeenkomst, tenzij een wettelijke wijziging
            anders vereist.
          </P>

          <H2>27. Toepasselijk recht</H2>
          <P>Op de diensten en overeenkomsten van Lenslopers is het Belgisch recht van toepassing.</P>
          <P>Voor consumenten blijven de dwingende wettelijke consumentenrechten van toepassing.</P>

          {/* Consent block */}
          <div className="mt-20 bg-ink text-white p-8 md:p-10 relative">
            <span className="absolute top-0 left-0 w-10 h-px bg-gold" />
            <span className="absolute top-0 left-0 h-10 w-px bg-gold" />
            <span className="absolute bottom-0 right-0 w-10 h-px bg-gold" />
            <span className="absolute bottom-0 right-0 h-10 w-px bg-gold" />

            <p className="eyebrow text-gold mb-4">Toestemming voor publicatie van foto's</p>
            <p className="text-white/70 text-sm mb-8 leading-relaxed">
              Deze toestemming staat volledig los van het akkoord met de algemene voorwaarden.
              Wanneer Lenslopers u vraagt om toestemming voor publicatie, kunt u onderstaande keuze
              maken:
            </p>

            <div className="space-y-6">
              <div className="border border-white/15 p-5">
                <p className="font-serif text-lg text-gold mb-2">☐ JA, ik geef toestemming</p>
                <p className="text-sm text-white/70 leading-relaxed">
                  Ik geef Lenslopers toestemming om herkenbare foto's waarop ik voorkom te gebruiken
                  voor de website, sociale media, portfolio, flyers, brochures, advertenties en
                  ander promotioneel materiaal van Lenslopers.
                </p>
              </div>

              <div className="border border-white/15 p-5">
                <p className="font-serif text-lg text-white mb-2">☐ NEE, ik geef geen toestemming</p>
                <p className="text-sm text-white/70 leading-relaxed">
                  Ik geef geen toestemming voor het gebruik van herkenbare foto's waarop ik voorkom
                  voor promotionele doeleinden.
                </p>
              </div>
            </div>

            <p className="mt-8 text-xs text-white/50 leading-relaxed">
              Ik begrijp dat deze toestemming vrijwillig is en dat ik een eerder gegeven
              toestemming kan intrekken via{" "}
              <a href={mailtoLink("Intrekking toestemming foto's")} className="text-gold hover:underline">
                {BRAND.email}
              </a>
              .
            </p>

            <div className="mt-10 pt-8 border-t border-white/15">
              <p className="eyebrow text-gold mb-4">Minderjarige</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm text-white/80">
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Naam minderjarige</p>
                  <div className="border-b border-white/25 h-6" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Naam ouder/wettelijke vertegenwoordiger</p>
                  <div className="border-b border-white/25 h-6" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Relatie tot minderjarige</p>
                  <div className="border-b border-white/25 h-6" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Datum</p>
                  <div className="border-b border-white/25 h-6" />
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Handtekening</p>
                  <div className="border-b border-white/25 h-10" />
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <H2>Contactgegevens</H2>
          <div className="bg-beige p-6 md:p-8 border-l-2 border-gold text-[15px] text-ink/80 leading-relaxed">
            <p className="font-serif text-lg text-ink mb-2">Lenslopers Fotografie</p>
            <p>Lemmens, Eric</p>
            <p>Ondernemingsnummer: BE0807.873.408</p>
            <p>
              E-mail:{" "}
              <a href={mailtoLink("Vraag")} className="text-gold hover:underline">
                {BRAND.email}
              </a>
            </p>
            <p>
              Website:{" "}
              <a href="https://www.lenslopers.com" className="text-gold hover:underline">
                www.lenslopers.com
              </a>
            </p>
            <p className="mt-3 text-xs text-ink/50 tracking-wider">
              Laatst bijgewerkt: 12 augustus 2026
            </p>
          </div>
        </motion.article>
      </section>
    </>
  );
};

export default Voorwaarden;
